import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Crown, Scale, BookOpen, Sparkles, Coins, ArrowRight, X,
  Share2, Copy, MessageCircle, Gift, Search, ScanLine, Users, Check,
} from "lucide-react";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PILLARS = [
  { key: "sovereignty", icon: Crown, title: "Sovereignty", line: "Own your data & identity. Nothing is sold, ever — it's yours." },
  { key: "equality", icon: Scale, title: "Equality", line: "Region-fair pricing. Everyone pays what's fair for where they live." },
  { key: "knowledge", icon: BookOpen, title: "Knowledge", line: "Ask anything, learn anything — courses, AI answers & a shared brain." },
  { key: "fun", icon: Sparkles, title: "Fun", line: "Communities, travel, meals & events — live and play as one family." },
  { key: "earn", icon: Coins, title: "Earn", line: "Turn your work, referrals & kindness into credits and real earnings." },
];

export default function OnboardingModal() {
  const { user, setUser } = useApp();
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [closing, setClosing] = useState(false);

  const show = user && user.consent && user.consent.terms && !user.onboarded && user.role !== "admin";
  if (!show || closing) return null;

  const ref = user.referral_code || "";
  const url = `${window.location.origin}/?ref=${ref}`;
  const msg = `I just joined My Kind 🌍 — one app for everything, built on sovereignty, equality, knowledge, fun & earning for all. Join me (you'll get 3 bonus credits): ${url}`;

  const finish = async (dest) => {
    setClosing(true);
    setUser((u) => (u ? { ...u, onboarded: true } : u));
    try { await api.post("/me/onboarded"); } catch { /* noop */ }
    if (dest) nav(dest);
  };

  const shareWa = () => window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  const copyLink = () => {
    const done = () => toast.success("Invite link copied ✨");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(done).catch(() => fallbackCopy(url, done));
    } else { fallbackCopy(url, done); }
  };
  const fallbackCopy = (text, done) => {
    try {
      const ta = document.createElement("textarea");
      ta.value = text; ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.focus(); ta.select();
      document.execCommand("copy"); document.body.removeChild(ta); done();
    } catch { toast.error("Couldn't copy — long-press the link to copy it."); }
  };
  const shareNative = async () => {
    if (navigator.share) { try { await navigator.share({ title: "My Kind", text: msg, url }); } catch { /* ignore */ } }
    else copyLink();
  };

  const quickWins = [
    { icon: Search, label: "Ask anXya — 1 free AI answer", dest: "/search" },
    { icon: ScanLine, label: "Scan a product — free", dest: "/personal?tab=receipts" },
    { icon: Users, label: "Meet the community", dest: "/feed" },
  ];

  return (
    <div className="fixed inset-0 z-[70] bg-black/75 backdrop-blur-md flex items-center justify-center p-4" data-testid="onboarding-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.96, y: 16 }} animate={{ opacity: 1, scale: 1, y: 0 }}
        className="rud-card rounded-3xl w-full max-w-lg max-h-[92vh] overflow-y-auto relative"
      >
        <button data-testid="onboarding-skip" onClick={() => finish()} className="absolute top-4 right-4 z-10 text-sec hover:text-[var(--rud-text)] transition-colors">
          <X size={18} />
        </button>

        {/* progress dots */}
        <div className="flex gap-1.5 px-7 pt-6">
          {[0, 1].map((i) => (
            <div key={i} className={`h-1 rounded-full transition-all duration-300 ${i === step ? "w-8 bg-brand" : "w-4 bg-white/15"}`} />
          ))}
        </div>

        <AnimatePresence mode="wait">
          {step === 0 ? (
            <motion.div key="welcome" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} className="p-7 pt-5">
              <div className="text-[11px] uppercase tracking-widest text-brand flex items-center gap-1.5"><Sparkles size={13} /> Welcome to the family</div>
              <h2 className="font-serif text-3xl tracking-tight mt-2 leading-tight" data-testid="onboarding-title">
                Namaste{user.name ? `, ${user.name.split(" ")[0]}` : ""} 🌍
              </h2>
              <p className="text-sec text-sm mt-2">
                My Kind gives <b className="text-[var(--rud-text)]">every person the power to do everything</b> — built on five simple promises.
              </p>

              <div className="mt-5 space-y-2.5">
                {PILLARS.map((p, i) => (
                  <motion.div
                    key={p.key}
                    initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.12 + i * 0.09 }}
                    className="flex items-start gap-3 rounded-2xl border border-rud bg-white/[0.03] p-3.5"
                    data-testid={`onboarding-pillar-${p.key}`}
                  >
                    <div className="w-10 h-10 rounded-xl bg-brand/10 text-brand grid place-items-center shrink-0">
                      <p.icon size={19} strokeWidth={1.8} />
                    </div>
                    <div>
                      <div className="text-[15px] font-semibold text-[var(--rud-text)]">{p.title}</div>
                      <div className="text-[13px] text-sec leading-snug">{p.line}</div>
                    </div>
                  </motion.div>
                ))}
              </div>

              <button data-testid="onboarding-continue" onClick={() => setStep(1)}
                className="btn-primary w-full h-12 rounded-full mt-6 text-sm font-medium flex items-center justify-center gap-2">
                Claim my first win <ArrowRight size={16} />
              </button>
              <button onClick={() => finish()} className="w-full text-center text-xs text-sec hover:text-brand mt-3 transition-colors">Skip for now</button>
            </motion.div>
          ) : (
            <motion.div key="firstwin" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -12 }} className="p-7 pt-5">
              <div className="text-[11px] uppercase tracking-widest text-brand flex items-center gap-1.5"><Sparkles size={13} /> Your first 60 seconds</div>
              <h2 className="font-serif text-2xl tracking-tight mt-2">Do one thing — it's on us ✨</h2>
              <p className="text-sec text-sm mt-1.5">Pick a quick win to feel the magic right now.</p>

              <div className="mt-4 space-y-2">
                {quickWins.map((q) => (
                  <button
                    key={q.dest}
                    data-testid={`onboarding-quickwin-${q.dest.replace(/[^a-z]/gi, "").toLowerCase()}`}
                    onClick={() => finish(q.dest)}
                    className="w-full flex items-center gap-3 rounded-2xl border border-rud bg-white/[0.03] p-3.5 text-left hover:border-brand/40 hover:bg-brand/5 transition-colors group"
                  >
                    <div className="w-9 h-9 rounded-xl bg-brand/10 text-brand grid place-items-center shrink-0"><q.icon size={17} /></div>
                    <span className="text-[14px] text-[var(--rud-text)] flex-1">{q.label}</span>
                    <ArrowRight size={15} className="text-sec group-hover:text-brand transition-colors" />
                  </button>
                ))}
              </div>

              {/* Viral invite card */}
              <div className="mt-5 rounded-2xl border border-brand/25 bg-brand/[0.06] p-4 relative overflow-hidden" data-testid="onboarding-invite">
                <div className="absolute -top-8 -right-6 w-32 h-32 brand-glow rounded-full pointer-events-none" />
                <div className="relative flex items-center gap-1.5 text-brand text-[11px] uppercase tracking-widest"><Gift size={13} /> Grow the family — everyone wins</div>
                <p className="relative text-sm text-sec mt-1.5">
                  Invite a friend: <b className="text-[var(--rud-text)]">they get 3 bonus credits</b>, and you earn credits + lifetime rewards when they thrive.
                </p>
                <div className="relative flex items-center gap-2 mt-3 rounded-xl bg-black/30 border border-rud px-3 py-2">
                  <Check size={14} className="text-brand shrink-0" />
                  <span className="text-[12px] text-[var(--rud-text)] truncate flex-1" data-testid="onboarding-invite-link">{url}</span>
                </div>
                <div className="relative flex flex-wrap gap-2 mt-3">
                  <button data-testid="onboarding-invite-share" onClick={shareNative} className="btn-primary rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Share2 size={14} /> Share</button>
                  <button data-testid="onboarding-invite-whatsapp" onClick={shareWa} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><MessageCircle size={14} /> WhatsApp</button>
                  <button data-testid="onboarding-invite-copy" onClick={copyLink} className="btn-ghost rounded-full h-9 px-4 text-sm flex items-center gap-1.5"><Copy size={14} /> Copy</button>
                </div>
              </div>

              <button data-testid="onboarding-finish" onClick={() => finish("/")}
                className="btn-primary w-full h-12 rounded-full mt-6 text-sm font-medium flex items-center justify-center gap-2">
                Start exploring <ArrowRight size={16} />
              </button>
              <button data-testid="onboarding-back" onClick={() => setStep(0)} className="w-full text-center text-xs text-sec hover:text-brand mt-3 transition-colors">Back</button>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </div>
  );
}
